/**
 * Utility functions for checking measurement values against target ranges
 */

/**
 * Get alert status for a measurement value
 * @param {number} value - The measurement value
 * @param {number} min - Minimum target value
 * @param {number} max - Maximum target value
 * @returns {string} - 'high', 'low', or 'normal'
 */
export const getMeasurementAlertStatus = (value, min, max) => {
  if (value === null || value === undefined || min == null || max == null) {
    return 'normal';
  }

  const numericValue = Number(value);
  const numericMin = Number(min);
  const numericMax = Number(max);

  if (
    Number.isNaN(numericValue) ||
    Number.isNaN(numericMin) ||
    Number.isNaN(numericMax)
  ) {
    return 'normal';
  }
  
  // Inclusive target boundaries:
  // value === min or value === max is considered normal.
  if (numericValue > numericMax) {
    return 'high';
  } else if (numericValue < numericMin) {
    return 'low';
  }
  return 'normal';
};

/**
 * Get CSS class for measurement value based on alert status
 * @param {string} status - 'high', 'low', or 'normal'
 * @returns {string} - CSS class name
 */
export const getMeasurementValueClass = (status) => {
  switch (status) {
    case 'high':
      return 'value-high';
    case 'low':
      return 'value-low';
    default:
      return 'value-normal';
  }
};

/**
 * Check if BP values are out of target range
 * @param {Object} bpData - Blood pressure data with systolic_pressure and diastolic_pressure
 * @param {Object} target - Target range with systolicMin, systolicMax, diastolicMin, diastolicMax
 * @returns {Object} - { sysStatus: 'high'|'low'|'normal', diaStatus: 'high'|'low'|'normal', isOutOfRange: boolean }
 */
export const checkBPValues = (bpData, target) => {
  if (!bpData || !target) {
    return { sysStatus: 'normal', diaStatus: 'normal', isOutOfRange: false };
  }

  const pickFirstNumber = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return null;
  };

  const sys = bpData.systolic_pressure;
  const dia = bpData.diastolic_pressure;
  const sysMin = pickFirstNumber(
    target.systolicMin,
    target.sysMin,
    target.systolic_min,
    target.systolic_pressure_min
  ) ?? 100;
  const sysMax = pickFirstNumber(
    target.systolicMax,
    target.sysMax,
    target.systolic_max,
    target.systolic_pressure_max
  ) ?? 140;
  const diaMin = pickFirstNumber(
    target.diastolicMin,
    target.diaMin,
    target.diastolic_min,
    target.diastolic_pressure_min
  ) ?? 60;
  const diaMax = pickFirstNumber(
    target.diastolicMax,
    target.diaMax,
    target.diastolic_max,
    target.diastolic_pressure_max
  ) ?? 90;
  
  const sysStatus = getMeasurementAlertStatus(sys, sysMin, sysMax);
  const diaStatus = getMeasurementAlertStatus(dia, diaMin, diaMax);
  const isOutOfRange = sysStatus !== 'normal' || diaStatus !== 'normal';
  
  return { sysStatus, diaStatus, isOutOfRange };
};

/**
 * Check if BG value is out of target range
 * @param {number} bgValue - Blood glucose value
 * @param {Object} target - Target range with min and max
 * @returns {string} - 'high', 'low', or 'normal'
 */
export const checkBGValue = (bgValue, target) => {
  if (bgValue === null || bgValue === undefined || !target) {
    return 'normal';
  }
  
  const min = target.minimum || target.min || 60;
  const max = target.maximum || target.max || 110;
  
  return getMeasurementAlertStatus(bgValue, min, max);
};

/**
 * Check if Weight value is out of target range
 * @param {number} weightKg - Weight in kilograms
 * @param {Object} target - Target range with weightMin and weightMax (in pounds)
 * @returns {string} - 'high', 'low', or 'normal'
 */
export const checkWeightValue = (weightKg, target) => {
  if (weightKg === null || weightKg === undefined || !target) {
    return 'normal';
  }
  
  // Convert weight from kg to lb for comparison
  const weightInLb = weightKg * 2.20462;
  const min = target.weightMin || 66;
  const max = target.weightMax || 220;
  
  return getMeasurementAlertStatus(weightInLb, min, max);
};

const getMeasurementDatePart = (measurement) =>
  measurement.measurement_date ||
  measurement.measure_new_date_time?.split('T')[0] ||
  measurement.created_at?.split('T')[0] ||
  '';

const getMeasurementTimePart = (measurement) =>
  measurement.measurement_time ||
  measurement.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
  measurement.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
  measurement.created_at?.split('T')[1]?.slice(0, 5) ||
  '';

const getMeasurementLatestStamp = (measurement) =>
  new Date(
    measurement.updated_at ||
    measurement.created_at ||
    measurement.measure_new_date_time ||
    measurement.measurement_date ||
    0
  ).getTime();

const getMeasurementValueSignature = (measurement) => {
  const pick = (value) => (value === undefined || value === null ? '' : String(value).trim());
  return [
    pick(measurement.systolic_pressure),
    pick(measurement.diastolic_pressure),
    pick(measurement.pulse),
    // BG may come in either normalized or raw DB shape.
    pick(measurement.blood_glucose_value),
    pick(measurement.blood_glucose_value_1),
    pick(measurement.blood_glucose_value_2),
    pick(measurement.blood_glucose_value_3),
    // Weight may come in either normalized or raw DB shape.
    pick(measurement.weight),
    pick(measurement.body_fat),
    pick(measurement.fat),
    pick(measurement.bmi)
  ].join('|');
};

/**
 * Keep latest only for exact duplicates:
 * - same date+time
 * - same reading values
 * If date+time are same but values differ, keep both.
 */
export const dedupeMeasurementsByDateTimeKeepLatest = (items = []) => {
  const sorted = [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const stampDiff = getMeasurementLatestStamp(b) - getMeasurementLatestStamp(a);
    if (stampDiff !== 0) return stampDiff;
    return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  });

  const seen = new Set();
  const deduped = [];

  sorted.forEach((measurement) => {
    const key = `${getMeasurementDatePart(measurement)}|${getMeasurementTimePart(measurement)}|${getMeasurementValueSignature(measurement)}`;
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(measurement);
  });

  return deduped;
};

/**
 * Format a Date object (or date value) to local 'YYYY-MM-DD' without UTC conversion.
 */
export const formatLocalDateString = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Compute fromDateParam and toDateParam for preset date ranges (e.g. 'Last 30 days').
 * Uses local calendar dates and includes tomorrow (+1 day buffer) on toDateParam
 * so that any readings recorded today are not excluded due to timezone offsets.
 */
export const getPresetDateRangeParams = (dateRangeString, daysDefault = 30) => {
  const days = parseInt(String(dateRangeString || '').replace(/\D/g, ''), 10) || daysDefault;
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return {
    fromDateParam: formatLocalDateString(startDate),
    toDateParam: formatLocalDateString(tomorrow)
  };
};

/**
 * Safely format 'YYYY-MM-DD' or ISO date strings as 'MM-DD-YYYY' without UTC day shifting.
 */
export const formatDateDisplay = (dateString) => {
  if (!dateString) return '';
  const datePart = String(dateString).trim().split('T')[0].split(' ')[0];
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, y, m, d] = match;
    return `${m}-${d}-${y}`;
  }
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};


